import React, { useState, useCallback, useMemo, useEffect } from 'react'
import {
    makeStyles,
    Typography,
    Paper,
    Box,
    IconButton,
    Dialog,
    DialogContent,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Checkbox,
    ListItemText,
    ListSubheader,
    TextField,
    InputAdornment,
    Chip,
} from '@material-ui/core'
import { ToggleButton, ToggleButtonGroup } from '@material-ui/lab'
import {
    Close as CloseIcon,
    Assessment as AssessmentIcon,
    Search as SearchIcon,
} from '@material-ui/icons'
import { useHistory, useLocation } from 'react-router-dom'
import { format } from 'date-fns'
import { useUser } from '../../hooks'
import { useAuditSnapshots } from '../../hooks/useAuditSnapshots'
import { SnapshotComparisonGrid } from './SnapshotComparisonGrid'
import { ProjectSnapshotDetailView } from './ProjectSnapshotDetailDialog'
import { PropertySnapshotDetailView } from './PropertySnapshotDetailDialog'
import { Container } from '../../components'
import {
    AuditSnapshotKind,
    AuditSnapshotRow,
    PROJECT_METRICS,
    PROPERTY_METRICS,
    ProjectSnapshotDetail,
    PropertySnapshotDetail,
} from './auditMetrics'

/** Celery weekly snapshots run Friday (day_of_week=5). */
const getNextWeeklyReportDate = (from: Date = new Date()): Date => {
    const result = new Date(from)
    result.setHours(0, 0, 0, 0)
    const day = result.getDay() // 0 Sun … 5 Fri
    const daysUntilFriday = (5 - day + 7) % 7
    // If today is Friday, show next week's Friday
    result.setDate(
        result.getDate() + (daysUntilFriday === 0 ? 7 : daysUntilFriday),
    )
    return result
}

const useStyles = makeStyles((theme) => ({
    root: {
        padding: theme.spacing(3),
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(2),
    },
    header: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1.5),
    },
    titleIcon: {
        color: theme.palette.text.secondary,
        fontSize: 28,
    },
    title: {
        fontWeight: 500,
    },
    paper: {
        padding: theme.spacing(2),
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(2),
    },
    controls: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(1.5),
    },
    filterRow: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1.5),
        flexWrap: 'wrap',
    },
    propertyFilter: {
        minWidth: 220,
        width: 240,
        flexShrink: 0,
    },
    chips: {
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: theme.spacing(0.75),
        flex: 1,
        minWidth: 0,
    },
    chip: {
        maxWidth: 200,
    },
    searchHeader: {
        backgroundColor: theme.palette.background.paper,
        lineHeight: 'normal',
        paddingTop: theme.spacing(1),
        paddingBottom: theme.spacing(1),
        zIndex: 1,
    },
    modeToggle: {
        backgroundColor: theme.palette.background.paper,
        border: `1px solid ${theme.palette.grey[300]}`,
        borderRadius: theme.shape.borderRadius,
        overflow: 'hidden',
        '& .MuiToggleButton-root': {
            textTransform: 'none',
            minWidth: 148,
            paddingLeft: theme.spacing(2.5),
            paddingRight: theme.spacing(2.5),
            paddingTop: theme.spacing(1),
            paddingBottom: theme.spacing(1),
            fontWeight: 500,
            border: 'none',
            '&.Mui-selected': {
                backgroundColor: theme.palette.primary.main,
                color: theme.palette.primary.contrastText,
                '&:hover': {
                    backgroundColor: theme.palette.primary.dark,
                },
            },
        },
    },
    dialogContent: {
        padding: 0,
        height: '80vh',
        width: '80vw',
    },
    dialogTitle: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: theme.spacing(1, 2),
    },
    closeButton: {
        marginLeft: 'auto',
    },
    iframe: {
        width: '100%',
        height: '100%',
        border: 'none',
    },
}))

const tabFromQuery = (search: string): AuditSnapshotKind => {
    const params = new URLSearchParams(search)
    return params.get('tab') === 'projects'
        ? 'project_analytics'
        : 'property_audit'
}

export const PropertyAudit: React.FC = () => {
    const classes = useStyles()
    const history = useHistory()
    const location = useLocation()
    const {
        rootUser,
        organization: userOrganization,
        workspaceUser,
    } = useUser()

    const kind = useMemo(() => tabFromQuery(location.search), [location.search])
    const [hasLoadedOnce, setHasLoadedOnce] = useState(false)
    const [viewerOpen, setViewerOpen] = useState(false)
    const [viewerUrl, setViewerUrl] = useState('')
    const [selectedPropertyIds, setSelectedPropertyIds] = useState<number[]>([])
    const [propertySearch, setPropertySearch] = useState('')
    const [detailRowId, setDetailRowId] = useState<string | number | null>(null)
    const [detailLabel, setDetailLabel] = useState<string | null>(null)
    const [detailLoading, setDetailLoading] = useState(false)
    const [detailError, setDetailError] = useState<string | null>(null)
    const [detail, setDetail] = useState<ProjectSnapshotDetail | null>(null)
    const [
        propertyDetail,
        setPropertyDetail,
    ] = useState<PropertySnapshotDetail | null>(null)
    const [detailMetrics, setDetailMetrics] = useState<Record<
        string,
        number | null
    > | null>(null)

    const organizationId =
        workspaceUser?.active_workspace?.organization ??
        userOrganization ??
        null

    const propertyOptions = useMemo(() => {
        if (!rootUser?.workspaces?.length) {
            return []
        }
        const seen = new Set<number>()
        const options: { id: number; name: string }[] = []
        for (const ws of rootUser.workspaces) {
            const company = ws.active_workspace
            if (
                company.company_type !== 'APARTMENT' ||
                seen.has(company.id) ||
                (organizationId != null &&
                    company.organization !== organizationId)
            ) {
                continue
            }
            seen.add(company.id)
            options.push({ id: company.id, name: company.name })
        }
        return options.sort((a, b) => a.name.localeCompare(b.name))
    }, [organizationId, rootUser?.workspaces])

    const propertyNameById = useMemo(() => {
        const map = new Map<number, string>()
        propertyOptions.forEach((p) => map.set(p.id, p.name))
        return map
    }, [propertyOptions])

    const filteredPropertyOptions = useMemo(() => {
        const q = propertySearch.trim().toLowerCase()
        if (!q) {
            return propertyOptions
        }
        return propertyOptions.filter((p) => p.name.toLowerCase().includes(q))
    }, [propertyOptions, propertySearch])

    const nextWeeklyLabel = useMemo(() => {
        const next = getNextWeeklyReportDate()
        return format(next, 'EEE, MMM d')
    }, [])

    const handleGenerateSuccess = useCallback(async () => {
        setHasLoadedOnce(true)
    }, [])

    const {
        rows,
        isLoading,
        isGeneratingProperty,
        isGeneratingProject,
        generateProgress,
        requestPropertyAudit,
        requestProjectReport,
        fetchProjectSnapshotDetail,
        fetchPropertySnapshotDetail,
    } = useAuditSnapshots({
        kind,
        organizationId,
        apartmentIds: selectedPropertyIds,
        useMock: false,
        onGenerateSuccess: handleGenerateSuccess,
    })

    useEffect(() => {
        if (!isLoading) {
            setHasLoadedOnce(true)
        }
    }, [isLoading])

    const handleModeChange = (
        _event: React.MouseEvent<HTMLElement>,
        value: AuditSnapshotKind | null,
    ) => {
        if (!value || value === kind) {
            return
        }
        // Leave report detail when switching tabs
        setDetailRowId(null)
        setDetailLabel(null)
        setDetail(null)
        setPropertyDetail(null)
        setDetailMetrics(null)
        setDetailError(null)
        const params = new URLSearchParams(location.search)
        if (value === 'project_analytics') {
            params.set('tab', 'projects')
        } else {
            params.delete('tab')
        }
        const search = params.toString()
        history.replace({
            pathname: location.pathname,
            search: search ? `?${search}` : '',
        })
    }

    const handlePropertyFilterChange = (
        event: React.ChangeEvent<{ value: unknown }>,
    ) => {
        setSelectedPropertyIds(event.target.value as number[])
    }

    const handleRemoveProperty = (propertyId: number) => {
        setSelectedPropertyIds((prev) => prev.filter((id) => id !== propertyId))
    }

    const handleGenerate = () => {
        if (kind === 'property_audit') {
            void requestPropertyAudit()
        } else {
            void requestProjectReport()
        }
    }

    const handleViewReport = (reportUrl: string) => {
        setViewerUrl(
            `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(
                reportUrl,
            )}`,
        )
        setViewerOpen(true)
    }

    const handleOpenDetail = (row: AuditSnapshotRow) => {
        setDetailRowId(row.id)
        setDetailLabel(row.label || null)
        setDetailMetrics(row.metrics)
        setDetailError(null)
        setDetail(null)
        setPropertyDetail(null)
    }

    const handleCloseDetail = () => {
        setDetailRowId(null)
        setDetailLabel(null)
        setDetail(null)
        setPropertyDetail(null)
        setDetailMetrics(null)
        setDetailError(null)
        setDetailLoading(false)
    }

    // Keep report detail in sync with property filter / refreshed rows.
    // Apply metrics + detail together after a successful fetch so the KPI
    // strip never drifts ahead of the properties / services table.
    useEffect(() => {
        if (detailRowId == null) {
            return
        }

        const matchingRow = rows.find((row) => row.id === detailRowId)

        let cancelled = false
        setDetailLoading(true)
        setDetailError(null)
        // Clear stale detail for the new scope while loading
        if (kind === 'property_audit') {
            setPropertyDetail(null)
            setDetail(null)
        } else {
            setDetail(null)
            setPropertyDetail(null)
        }

        if (kind === 'property_audit') {
            void fetchPropertySnapshotDetail(detailRowId)
                .then((payload) => {
                    if (cancelled) {
                        return
                    }
                    setPropertyDetail(payload)
                    setDetail(null)
                    if (matchingRow) {
                        setDetailMetrics(matchingRow.metrics)
                        setDetailLabel(
                            matchingRow.label || payload.label || null,
                        )
                    } else if (payload.label) {
                        setDetailLabel(payload.label)
                    }
                })
                .catch((e: any) => {
                    if (!cancelled) {
                        setPropertyDetail(null)
                        setDetailError(
                            e?.response?.data?.error ||
                                e?.message ||
                                'Unable to load report detail',
                        )
                    }
                })
                .finally(() => {
                    if (!cancelled) {
                        setDetailLoading(false)
                    }
                })
        } else {
            void fetchProjectSnapshotDetail(detailRowId)
                .then((payload) => {
                    if (cancelled) {
                        return
                    }
                    setDetail(payload)
                    setPropertyDetail(null)
                    if (matchingRow) {
                        setDetailMetrics(matchingRow.metrics)
                        setDetailLabel(matchingRow.label || null)
                    }
                })
                .catch((e: any) => {
                    if (!cancelled) {
                        setDetail(null)
                        setDetailError(
                            e?.response?.data?.error ||
                                e?.message ||
                                'Unable to load report detail',
                        )
                    }
                })
                .finally(() => {
                    if (!cancelled) {
                        setDetailLoading(false)
                    }
                })
        }

        return () => {
            cancelled = true
        }
    }, [
        detailRowId,
        kind,
        rows,
        fetchProjectSnapshotDetail,
        fetchPropertySnapshotDetail,
    ])

    const isDetailView = detailRowId != null

    const isGenerating =
        kind === 'property_audit' ? isGeneratingProperty : isGeneratingProject

    const metrics =
        kind === 'property_audit' ? PROPERTY_METRICS : PROJECT_METRICS

    const showInitialLoading = isLoading && !hasLoadedOnce && rows.length === 0

    const progressPercent =
        generateProgress && generateProgress.total > 0
            ? Math.min(
                  100,
                  (generateProgress.current / generateProgress.total) * 100,
              )
            : 0

    const progressLabel = generateProgress?.message
        ? generateProgress.message
        : generateProgress
        ? `Processing ${generateProgress.current}/${generateProgress.total} properties`
        : isGenerating
        ? 'Starting...'
        : ''

    const generateLabel =
        kind === 'property_audit'
            ? 'Generate Property Audit'
            : 'Generate Project Report'

    return (
        <Container className={classes.root}>
            <Box className={classes.header}>
                <AssessmentIcon className={classes.titleIcon} />
                <Typography variant="h5" className={classes.title}>
                    Audits & Reports
                </Typography>
            </Box>

            <Paper className={classes.paper} elevation={0}>
                <Box className={classes.controls}>
                    <Box className={classes.filterRow}>
                        <FormControl
                            variant="outlined"
                            size="small"
                            className={classes.propertyFilter}
                        >
                            <InputLabel id="audit-property-filter-label">
                                Property
                            </InputLabel>
                            <Select
                                labelId="audit-property-filter-label"
                                multiple
                                value={selectedPropertyIds}
                                onChange={handlePropertyFilterChange}
                                onClose={() => setPropertySearch('')}
                                label="Property"
                                MenuProps={{
                                    autoFocus: false,
                                    PaperProps: { style: { maxHeight: 360 } },
                                    getContentAnchorEl: null,
                                }}
                                renderValue={(selected) => {
                                    const ids = selected as number[]
                                    if (ids.length === 0) {
                                        return 'All'
                                    }
                                    if (ids.length === 1) {
                                        return '1 property'
                                    }
                                    return `${ids.length} properties`
                                }}
                            >
                                <ListSubheader className={classes.searchHeader}>
                                    <TextField
                                        size="small"
                                        autoFocus
                                        placeholder="Search properties..."
                                        fullWidth
                                        variant="outlined"
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <SearchIcon fontSize="small" />
                                                </InputAdornment>
                                            ),
                                        }}
                                        value={propertySearch}
                                        onChange={(e) =>
                                            setPropertySearch(e.target.value)
                                        }
                                        onKeyDown={(e) => e.stopPropagation()}
                                        onClick={(e) => e.stopPropagation()}
                                    />
                                </ListSubheader>
                                {filteredPropertyOptions.length === 0 ? (
                                    <ListSubheader disableSticky>
                                        No properties match
                                    </ListSubheader>
                                ) : (
                                    filteredPropertyOptions.map((property) => (
                                        <MenuItem
                                            key={property.id}
                                            value={property.id}
                                        >
                                            <Checkbox
                                                checked={selectedPropertyIds.includes(
                                                    property.id,
                                                )}
                                                color="primary"
                                                size="small"
                                                style={{
                                                    padding: 2,
                                                    marginRight: 4,
                                                }}
                                            />
                                            <ListItemText
                                                primary={property.name}
                                            />
                                        </MenuItem>
                                    ))
                                )}
                            </Select>
                        </FormControl>

                        {selectedPropertyIds.length > 0 && (
                            <Box className={classes.chips}>
                                {selectedPropertyIds.map((id) => (
                                    <Chip
                                        key={id}
                                        size="small"
                                        label={propertyNameById.get(id) ?? id}
                                        className={classes.chip}
                                        onDelete={() =>
                                            handleRemoveProperty(id)
                                        }
                                    />
                                ))}
                            </Box>
                        )}
                    </Box>

                    <ToggleButtonGroup
                        exclusive
                        value={kind}
                        onChange={handleModeChange}
                        size="small"
                        className={classes.modeToggle}
                    >
                        <ToggleButton value="property_audit">
                            Property
                        </ToggleButton>
                        <ToggleButton value="project_analytics">
                            Projects
                        </ToggleButton>
                    </ToggleButtonGroup>
                </Box>

                {isDetailView ? (
                    kind === 'property_audit' ? (
                        <PropertySnapshotDetailView
                            key={String(detailRowId)}
                            onBack={handleCloseDetail}
                            reportLabel={detailLabel}
                            metrics={detailMetrics}
                            detail={propertyDetail}
                            isLoading={detailLoading || isLoading}
                            errorMessage={detailError}
                            onViewReport={handleViewReport}
                        />
                    ) : (
                        <ProjectSnapshotDetailView
                            onBack={handleCloseDetail}
                            detail={detail}
                            metrics={detailMetrics}
                            isLoading={detailLoading || isLoading}
                            errorMessage={detailError}
                        />
                    )
                ) : (
                    <SnapshotComparisonGrid
                        rows={rows}
                        metrics={metrics}
                        isLoading={showInitialLoading}
                        isRefreshing={isLoading && hasLoadedOnce}
                        emptyMessage={
                            kind === 'property_audit'
                                ? 'No property audit snapshots yet. Generate a property audit or wait for the weekly job.'
                                : 'No project analytics snapshots yet. Generate a project report or wait for the weekly job.'
                        }
                        onView={handleViewReport}
                        onOpenDetail={handleOpenDetail}
                        generateLabel={generateLabel}
                        onGenerate={handleGenerate}
                        isGenerating={isGenerating}
                        showGenerateProgress={
                            isGenerating && kind === 'property_audit'
                        }
                        generateProgressPercent={progressPercent}
                        generateProgressLabel={progressLabel}
                        nextWeeklyLabel={nextWeeklyLabel}
                    />
                )}
            </Paper>

            <Dialog
                open={viewerOpen}
                onClose={() => setViewerOpen(false)}
                maxWidth="xl"
                fullWidth
            >
                <div className={classes.dialogTitle}>
                    <Typography variant="h6">Report Viewer</Typography>
                    <IconButton
                        className={classes.closeButton}
                        onClick={() => setViewerOpen(false)}
                    >
                        <CloseIcon />
                    </IconButton>
                </div>
                <DialogContent className={classes.dialogContent}>
                    <iframe
                        src={viewerUrl}
                        className={classes.iframe}
                        title="Excel Report Viewer"
                    />
                </DialogContent>
            </Dialog>
        </Container>
    )
}
